import { Component, computed, input, Input, linkedSignal, OnDestroy, signal, inject } from '@angular/core';
import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../../../store/app/app.selectors';
import { Subscription } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastService } from '../../../../_services/toast-service/toast.service';
import { Card, CardBalance, CardBlockReason, CardStatus, CardType } from '../../../../_interfaces/cards.interface';
import { BoFormsModule } from '@bo/ng-forms';
import { VirtualCardIframeComponent } from '../virtual-card-iframe/virtual-card-iframe.component';
import { CardsApiService } from '../../../../_services/cards-service/cards-api/cards-api.service';
import { OffcanvasBaseComponent } from '../../offcanvas/offcanvas-base/offcanvas-base.component';
import { VirtualCardStaticComponent } from '../virtual-card-static/virtual-card-static.component';
import { StatusBadgeComponent, statusBadgeDepartments } from '../../status-badge/status-badge.component';
import { AvatarComponent } from '../../avatar/avatar.component';
import { EmptyIndicatorPipe } from '../../../pipes/empty-indicator.pipe';

@Component({
  selector: 'app-view-cards',
  imports: [
    BoFormsModule,
    VirtualCardIframeComponent,
    OffcanvasBaseComponent,
    VirtualCardStaticComponent,
    StatusBadgeComponent,
    AvatarComponent,
    DatePipe,
    CurrencyPipe,
    EmptyIndicatorPipe,
    NgTemplateOutlet,
  ],
  templateUrl: './view-card.component.html',
})
export class ViewCardComponent implements OnDestroy {
  readonly store = inject(Store);
  readonly offcanvasService = inject(OffcanvasService);
  readonly clipboard = inject(Clipboard);
  readonly toastService = inject(ToastService);
  readonly cardsApiService = inject(CardsApiService);

  readonly userEmail$ = this.store.select(AppSelectors.userEmail);
  @Input({ required: true }) title: string | undefined;
  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  readonly subscriptions = new Subscription();

  // Read-only Input
  cardDetail = input.required<Card>();
  cardBalance = input.required<CardBalance>();

  // Linked to input, but locally updatable
  cardDetailDisplay = linkedSignal(() => this.cardDetail());
  signedInUserEmail = signal('');

  isReasonOther = computed(() => this.cardDetailDisplay()?.block?.block_reason === CardBlockReason.Other);
  isSubscriptionCard = computed(() => this.cardDetailDisplay()?.type === CardType.Subscription);
  returnUrl = computed(() => {
    const type = this.cardDetailDisplay().type;
    if (type === CardType.Personal) return 'cards/employee-cards';
    if (type === CardType.Subscription) return 'cards/subscription-cards';
    return '';
  });

  isOwner = computed(
    () => !!this.signedInUserEmail() && this.signedInUserEmail() === this.cardDetailDisplay()?.holder?.email,
  );

  isBlockedCard = computed(() => this.cardDetailDisplay()?.status === CardStatus.Blocked);
  isRemovedCard = computed(() => this.cardDetailDisplay()?.status === CardStatus.Removed);
  isActiveCard = computed(() => this.cardDetailDisplay()?.status === CardStatus.Active);

  constructor() {
    this.subscriptions.add(
      this.userEmail$.subscribe((email) => {
        if (email) this.signedInUserEmail.set(email);
      }),
    );
  }

  onUnblockCard() {
    const cardId = this.cardDetailDisplay().id;
    const isPersonal = this.cardDetailDisplay().type === CardType.Personal;

    this.subscriptions.add(
      this.cardsApiService.unblockCard(cardId).subscribe({
        next: () => {
          const apiCall$ = isPersonal
            ? this.cardsApiService.getCardPersonalById(cardId)
            : this.cardsApiService.getSubscriptionCardById(cardId);

          this.subscriptions.add(
            apiCall$.subscribe({
              next: (resp) => {
                // Update the display signal
                this.cardDetailDisplay.set(resp);
                const typeLabel = isPersonal ? 'an employee card' : 'a subscription card';
                this.toastService.show(`You have unblocked ${typeLabel}`, 'iconoir-check-circle');
              },
            }),
          );
        },
        error: (err) => this.toastService.show(`Something went wrong. ${err.error.card}`, 'iconoir-xmark'),
      }),
    );
  }

  goToBlockCard() {
    this.offcanvasService.offCanvasNavigate(`${this.returnUrl()}/block-card/${this.cardDetailDisplay()?.id}`);
  }

  goToReplaceCard() {
    this.offcanvasService.offCanvasNavigate(`${this.returnUrl()}/replace-card/${this.cardDetailDisplay()?.id}`);
  }

  goToRemoveCard() {
    this.offcanvasService.offCanvasNavigate(`${this.returnUrl()}/remove-card/${this.cardDetail().id}`);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly CardBlockReason = CardBlockReason;
}
